import React from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import CustomToolTipHost from "../../../../components/CustomToolTipHost/CustomToolTipHost";
import { AI_DRAFT_STATE, friendlyDraftError } from "./aiDraftState";
import "./AiDraft.css";

export const AiDraftIcon = ({ color = "currentColor", size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 3h9l5 5v13H5z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M8 13h5M8 16.5h7" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M17.5 1.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"
      fill={color}
    />
  </svg>
);

/**
 * @param job     the latest generation
 * @param running how many generations are still running
 */
export const aiDraftTooltip = (state, job, running = 0) => {
  switch (state) {
    case AI_DRAFT_STATE.NO_KEY_IMAGES:
      return "Mark key images to generate an AI draft";
    case AI_DRAFT_STATE.GENERATING:
      return running > 1 ? `Generating ${running} AI drafts…` : "Generating AI draft…";
    case AI_DRAFT_STATE.READY:
      return "AI draft ready: click to review";
    case AI_DRAFT_STATE.VIEWED:
      return "Open AI drafts";
    case AI_DRAFT_STATE.FAILED:
      return `AI draft failed${job?.error ? `: ${friendlyDraftError(job.error)}` : ""}`;
    case AI_DRAFT_STATE.STALE:
      return "Key images changed since the last AI draft";
    default:
      return "Generate AI draft report";
  }
};

/**
 * Viewer toolbar button for the AI Draft Report. Idle opens the panel to pick a model and
 * generate (onGenerate); every other state opens the panel with one tab per generation (onOpen).
 * It glows (pulsing box-shadow in the accent colour, static with reduced motion) while a finished
 * draft is unread, and a small count shows how many generations are still running.
 */
const AiDraftButton = ({ aiDraft, onGenerate, onOpen, color }) => {
  const { state, job, announcement, refresh, busy, runningCount = 0 } = aiDraft;
  if (state === AI_DRAFT_STATE.HIDDEN) return null;

  const disabled = state === AI_DRAFT_STATE.NO_KEY_IMAGES || busy;
  const tooltip = aiDraftTooltip(state, job, runningCount);

  const handleClick = () => {
    if (disabled) return;
    if (state === AI_DRAFT_STATE.IDLE) onGenerate();
    else onOpen();
  };

  return (
    <CustomToolTipHost content={tooltip} id="ai-draft">
      <div
        className={`highlight-tool ai-draft-btn ai-draft-btn--${state}${state === AI_DRAFT_STATE.READY ? " ai-draft-btn--glow" : ""}${disabled ? " ai-draft-btn--disabled" : " cursor-pointer"}`}
        role="button"
        tabIndex={0}
        aria-label={tooltip}
        aria-disabled={disabled}
        data-testid="ai-draft-button"
        data-state={state}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        // the key-image count may have changed since load (KO marked, captures enabled)
        onMouseEnter={state === AI_DRAFT_STATE.NO_KEY_IMAGES ? refresh : undefined}
      >
        {state === AI_DRAFT_STATE.GENERATING ? (
          <Spinner size={SpinnerSize.small} ariaLabel="Generating AI draft" />
        ) : (
          <AiDraftIcon color={state === AI_DRAFT_STATE.FAILED ? "#E5484D" : color} />
        )}
        {runningCount > 0 && (
          <span className="ai-draft-progress" data-testid="ai-draft-running-count" title={`${runningCount} running`}>
            {runningCount}
          </span>
        )}
        {state === AI_DRAFT_STATE.READY && <span className="ai-draft-badge ai-draft-badge--ready" />}
        {state === AI_DRAFT_STATE.STALE && <span className="ai-draft-badge ai-draft-badge--stale" />}
        {state === AI_DRAFT_STATE.FAILED && <span className="ai-draft-badge ai-draft-badge--failed">!</span>}
        {state === AI_DRAFT_STATE.VIEWED && <span className="ai-draft-badge ai-draft-badge--viewed">✓</span>}
      </div>
      <span className="ai-draft-sr-only" aria-live="polite" role="status">
        {announcement}
      </span>
    </CustomToolTipHost>
  );
};

export default AiDraftButton;
